import { FC, useMemo, useCallback, ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { DefinedUseQueryResult } from '@tanstack/react-query'
import { FormApi } from 'final-form'

import { Tabs, ISafetyCaseHazardValues, ISafetyCaseHazardUpsertValues } from '../../../components'
import ContentMapLayout from '../../../hoc/content-map-layout'
import Map from '../../../components/map/map'
import MarkerLayout from '../../../components/map/marker-layout'
import PolygonLayout from '../../../components/map/polygon-layout'

import { getGeoJson, markerToGeoJson, polygonToGeoJson } from '../../../util'
import { useSafeQueryParams } from '../../../hooks'
import { hazardQueryParamsToParams } from '../safety-case-page/safety-cases-info-utils'
import { DEFAULT_MARKER_COLOR } from '../../../components/map/hooks/use-marker-icons'

import { ISafetyCaseHazard, IGeoJson } from '../../../interfaces'
import SafetyCaseHazardInfo from './safety-case-hazard-info'
import SafetyCaseHazardMitigations from './safety-case-hazard-mitigations'

type HazardBuilderType = {
  hasWriteAccess?: boolean
  handleSaveHazard: (safetyCaseHazardValues: ISafetyCaseHazardValues) => void
  hazardQuery?: DefinedUseQueryResult<ISafetyCaseHazard | undefined, Error>
  setHeaderControls: (headerControls?: ReactElement) => void
  saveHeaderControls: ReactElement
  setHazardGeoJson: (geoJson: IGeoJson | undefined) => void
  hazardGeoJson?: IGeoJson
  setSaveHazardForm?: (saveSafetyHazardForm: FormApi<ISafetyCaseHazardValues>) => void
  saveHazardInitialValues?: ISafetyCaseHazardUpsertValues
  setSaveHazardValues?: (safetyCaseHazardUpsertValues: ISafetyCaseHazardUpsertValues) => void
  isSaveFormDisabled?: boolean
}

const SafetyCaseHazardBuilder: FC<HazardBuilderType> = props => {
  const {
    hasWriteAccess,
    handleSaveHazard,
    hazardQuery,
    setHeaderControls,
    setHazardGeoJson,
    saveHeaderControls,
    hazardGeoJson,
    setSaveHazardForm,
    saveHazardInitialValues,
    setSaveHazardValues,
    isSaveFormDisabled,
  } = props

  const location = useLocation()

  const safetyCase = hazardQuery?.data

  const isHazardFilled = useMemo(() => Boolean(hazardQuery?.data?.name), [hazardQuery?.data])

  const mitigationsCount = hazardQuery?.data?.mitigations?.length

  const routes = useMemo(
    () => [
      { to: 'info', label: 'General', allowedClaims: '*', replace: true },
      {
        to: 'mitigations',
        label: `Mitigations (${mitigationsCount})`,
        disabled: !isHazardFilled,
        allowedClaims: '*',
        replace: true,
        search: location.search,
      },
    ],
    [isHazardFilled, location.search, mitigationsCount],
  )

  const { params, setParams } = useSafeQueryParams(hazardQueryParamsToParams)

  const hazardMitigationsPagination = useMemo(() => params.mitigations.pagination, [params.mitigations.pagination])

  const setHazardMitigationsPagination = useCallback(
    (values: { [key: string]: number }) => {
      setParams({
        mitigations: {
          pagination: {
            ...hazardMitigationsPagination,
            ...values,
          },
        },
      })
    },
    [setParams, hazardMitigationsPagination],
  )

  const { markersGeoJson: safetyCaseMarkersGeoJson, polygonsGeoJson: safetyCasePolygonsGeoJson } = getGeoJson(
    hazardGeoJson ? [hazardGeoJson] : [],
    (geoJson: IGeoJson) =>
      safetyCase?.id
        ? { ...geoJson, properties: { ...(geoJson.properties || {}), id: safetyCase.id } }
        : geoJson,
  )

  const onSafetyCaseMarkerComplete = useCallback(
    (marker: google.maps.Marker) => {
      if (isSaveFormDisabled) {
        marker.setMap(null)
        return
      }

      const newGeoJson = markerToGeoJson(marker, DEFAULT_MARKER_COLOR)

      if (!newGeoJson) {
        return
      }

      setHazardGeoJson(newGeoJson)
      marker.setMap(null)
    },
    [isSaveFormDisabled, setHazardGeoJson],
  )

  const onSafetyCasePolygonComplete = useCallback(
    (polygon: google.maps.Polygon) => {
      if (isSaveFormDisabled) {
        polygon.setMap(null)
        return
      }

      const newGeoJson = polygonToGeoJson(polygon, DEFAULT_MARKER_COLOR)

      if (!newGeoJson) {
        return
      }

      setHazardGeoJson(newGeoJson)

      polygon.setMap(null)
    },
    [setHazardGeoJson, isSaveFormDisabled],
  )

  return (
    <ContentMapLayout>
      {{
        content: (
          <div className="d-flex flex-column w-100">
            {routes.filter(route => !route.disabled).length > 1 && (
              <Tabs routes={routes.filter(route => !route.disabled)} />
            )}

            <Routes>
              <Route index element={<Navigate to="info" replace />} />

              <Route
                path="/info"
                element={
                  <SafetyCaseHazardInfo
                    hasWriteAccess={hasWriteAccess}
                    setHeaderControls={setHeaderControls}
                    setHazardGeoJson={setHazardGeoJson}
                    handleSaveHazard={handleSaveHazard}
                    saveHeaderControls={saveHeaderControls}
                    setSaveHazardForm={setSaveHazardForm}
                    saveHazardInitialValues={saveHazardInitialValues}
                    setSaveHazardValues={setSaveHazardValues}
                    isSaveFormDisabled={isSaveFormDisabled}
                  />
                }
              />

              {safetyCase && isHazardFilled && (
                <Route
                  path="/mitigations"
                  element={
                    <SafetyCaseHazardMitigations
                      hasWriteAccess={hasWriteAccess}
                      hazardQuery={hazardQuery}
                      setHeaderControls={setHeaderControls}
                      hazardMitigationsPagination={hazardMitigationsPagination}
                      setHazardMitigationsPagination={setHazardMitigationsPagination}
                    />
                  }
                />
              )}

              <Route path="*" element={hazardQuery ? null : <Navigate to="info" replace />} />
            </Routes>
          </div>
        ),
        map: (
          <Map
            drawingManagerOptions={{
              onMarkerComplete: onSafetyCaseMarkerComplete,
              onPolygonComplete: onSafetyCasePolygonComplete,
            }}
          >
            <MarkerLayout geoJson={safetyCaseMarkersGeoJson} clickable={false} />

            <PolygonLayout geoJson={safetyCasePolygonsGeoJson} clickable={false} />
          </Map>
        ),
      }}
    </ContentMapLayout>
  )
}

export default SafetyCaseHazardBuilder
